import React from 'react';
import { motion } from 'framer-motion';

// --- Data for QA Tools ---
const toolCategories = [
    {
        name: 'Test Management & Bug Tracking',
        description: 'Tools to organize test cases, execute test runs, and track defects.',
        tools: [
            { name: 'JIRA', description: 'The industry-standard tool for agile project management and issue tracking.', link: 'https://www.atlassian.com/software/jira' },
            { name: 'TestRail', description: 'Comprehensive test case management to organize, track, and manage testing efforts.', link: 'https://www.gurock.com/testrail/' },
            { name: 'Zephyr', description: 'A popular test management solution that integrates directly inside JIRA.', link: 'https://www.getzephyr.com/' },
        ]
    },
    {
        name: 'Web Automation Testing',
        description: 'Frameworks and libraries for automating web browser interactions.',
        tools: [
            { name: 'Selenium', description: 'The most popular open-source framework for automating web browsers.', link: 'https://www.selenium.dev/' },
            { name: 'Cypress', description: 'A modern, all-in-one testing framework for anything that runs in a browser.', link: 'https://www.cypress.io/' },
            { name: 'Playwright', description: 'A powerful and reliable end-to-end testing framework from Microsoft.', link: 'https://playwright.dev/' },
        ]
    },
    // --- NEW SECTION ADDED HERE ---
    {
        name: 'AI-Powered Testing',
        description: 'Leverage artificial intelligence to accelerate test creation, reduce maintenance, and improve test coverage.',
        tools: [
            { name: 'Applitools', description: 'An AI-powered platform for visual testing and monitoring to catch UI bugs automatically.', link: 'https://applitools.com/' },
            { name: 'Mabl', description: 'A low-code test automation platform that uses AI for self-healing tests and maintenance.', link: 'https://www.mabl.com/' },
            { name: 'Katalon Studio', description: 'A comprehensive solution with built-in AI features to enhance test reliability and speed.', link: 'https://www.katalon.com/' },
        ]
    },
    {
        name: 'API Testing',
        description: 'Tools for testing and interacting with Application Programming Interfaces (APIs).',
        tools: [
            { name: 'Postman', description: 'The complete platform for building, testing, and documenting APIs.', link: 'https://www.postman.com/' },
            { name: 'REST Assured', description: 'A Java library that simplifies testing RESTful web services.', link: 'https://rest-assured.io/' },
            { name: 'Insomnia', description: 'A collaborative, open-source API design and testing tool.', link: 'https://insomnia.rest/' },
        ]
    },
    {
        name: 'Performance Testing',
        description: 'Tools to measure the speed, stability, and responsiveness of applications under a workload.',
        tools: [
            { name: 'JMeter', description: 'An open-source Java application designed for load testing and performance measurement.', link: 'https://jmeter.apache.org/' },
            { name: 'Gatling', description: 'A powerful, open-source load testing tool focused on ease of use and high performance.', link: 'https://gatling.io/' },
        ]
    }
];

// --- Animation Variants ---
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const cardHover = {
    scale: 1.05,
    y: -5,
    boxShadow: "0px 15px 25px rgba(0, 0, 0, 0.1)",
    transition: { type: "spring", stiffness: 400, damping: 10 }
};

const ToolsPage = () => {
    return (
        <div className="space-y-20">
            {/* Page Header */}
            <motion.div
                className="text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                    Essential QA & Testing Tools
                </h1>
                <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-600">
                    A curated list of industry-standard tools to master for a successful career in Quality Assurance.
                </p>
            </motion.div>

            {/* Tool Sections */}
            <div className="space-y-16">
                {toolCategories.map((category) => (
                    <motion.section
                        key={category.name}
                        variants={sectionVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        <div className="border-b border-gray-200 pb-5 mb-8">
                            <h2 className="text-3xl font-bold text-gray-900">{category.name}</h2>
                            <p className="mt-2 text-lg text-gray-500">{category.description}</p>
                        </div>
                        <motion.div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                            variants={staggerContainer}
                        >
                            {category.tools.map((tool) => (
                                <motion.div
                                    key={tool.name}
                                    className="bg-white rounded-xl shadow-lg p-6 flex flex-col border border-transparent hover:border-indigo-500"
                                    variants={cardVariants}
                                    whileHover={cardHover}
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-100 flex-shrink-0 mr-4 flex items-center justify-center font-bold text-2xl text-indigo-600">
                                            {tool.name.charAt(0)}
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900">{tool.name}</h3>
                                        </div>
                                    </div>
                                    <p className="text-gray-600 flex-grow">{tool.description}</p>
                                    <a
                                        href={tool.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-4 font-semibold text-indigo-600 hover:text-indigo-500 self-start group"
                                    >
                                        Learn More <span className="inline-block transform group-hover:translate-x-1 transition-transform">&rarr;</span>
                                    </a>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.section>
                ))}
            </div>
        </div>
    );
};

export default ToolsPage;